import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, TrendingUp, AlertTriangle, ShoppingCart, DollarSign, BarChart3, CheckCircle2, Circle, Phone, ChevronRight, Clock, UserX, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { NeverOutAlert } from '../components/NeverOutAlert';
import { TillTodayCard } from '../components/TillTodayCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Upcoming Time Off banner — shows pending shift alerts whose `shift_date` is
// within the next 7 days. Each banner is dismissible PER DAY (localStorage keyed
// by alert id + today's date), so it comes back tomorrow until the leave date
// passes or the alert is resolved on the Roster page.
const UpcomingLeaveBanner = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [dismissedToday, setDismissedToday] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    const out = new Set();
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(`dismissed_leave_`) && k.endsWith(`_${today}`)) {
          out.add(k.replace(`dismissed_leave_`, '').replace(`_${today}`, ''));
        }
      }
    } catch (_) {}
    return out;
  });

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/shift-alerts?status=pending`, { headers: getAuthHeader() });
      setAlerts(res.data || []);
    } catch (_) { /* silent */ }
  }, [getAuthHeader]);

  useEffect(() => {
    fetch();
    const onChange = () => fetch();
    window.addEventListener('shift-alerts-changed', onChange);
    return () => window.removeEventListener('shift-alerts-changed', onChange);
  }, [fetch]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysOut = new Date(today);
  sevenDaysOut.setDate(today.getDate() + 7);

  const upcoming = alerts.filter(a => {
    if (!a.shift_date || !/^\d{4}-\d{2}-\d{2}$/.test(a.shift_date)) return false;
    const d = new Date(a.shift_date + 'T00:00:00');
    return d >= today && d <= sevenDaysOut && !dismissedToday.has(a.id);
  });

  if (upcoming.length === 0) return null;

  const dismiss = (id) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    try { localStorage.setItem(`dismissed_leave_${id}_${todayStr}`, '1'); } catch (_) {}
    setDismissedToday(prev => new Set(prev).add(id));
  };

  return (
    <div className="space-y-2" data-testid="upcoming-leave-banner">
      {upcoming.map(a => {
        const d = new Date(a.shift_date + 'T00:00:00');
        const daysUntil = Math.round((d - today) / 86400000);
        const dayLabel = daysUntil === 0 ? 'TODAY' : daysUntil === 1 ? 'TOMORROW' : `in ${daysUntil} days`;
        const dateStr = d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });
        return (
          <div
            key={a.id}
            className="rounded-lg border-2 border-red-400 bg-red-50 px-4 py-3 flex items-start gap-3 shadow-sm"
            data-testid={`leave-banner-${a.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shrink-0">
              <UserX className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-red-900 text-base leading-tight">
                <span className="uppercase">{a.away_staff}</span> is OFF {dayLabel} — {dateStr}
              </p>
              <p className="text-sm text-red-800 mt-0.5">
                {a.shift_time && <span className="font-semibold">{a.shift_time} · </span>}
                {a.reason && <span>{a.reason}</span>}
                {a.store && <span className="ml-1 text-xs uppercase tracking-wider text-red-700">· {a.store}</span>}
                {!a.covered_by && <span className="ml-2 font-bold">— no cover assigned yet</span>}
                {a.covered_by && <span className="ml-2">— covered by <b>{a.covered_by}</b></span>}
              </p>
              {a.notes && <p className="text-xs text-red-700 mt-1 italic">"{a.notes}"</p>}
              <button
                onClick={() => navigate('/weekly-roster')}
                className="text-xs font-semibold text-red-700 hover:text-red-900 underline mt-1"
                data-testid={`leave-banner-roster-link-${a.id}`}
              >
                Open Roster to assign cover →
              </button>
            </div>
            <button
              onClick={() => dismiss(a.id)}
              className="p-1.5 rounded hover:bg-red-100 text-red-700"
              title="Dismiss for today (will reappear tomorrow)"
              data-testid={`leave-banner-dismiss-${a.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};



// Compact "Today's Tasks" widget pinned to the top of the Dashboard so staff
// see what's expected the moment they open the till. Shows progress, the staff
// on shift right now, the top 5 unfinished tasks (priority + customer-call first),
// and a "View all" link. Tapping a circle checks it off inline.
const TodaysTasksWidget = ({ store }) => {
  const { getAuthHeader, user, activeStaff } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/staff/tasks/today?store=${store}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (_) { /* silent — widget is best-effort */ }
  }, [getAuthHeader, store]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Quick-complete — but ALWAYS prompt for the staff member's name so tasks
  // aren't silently attributed to the owner account. Pre-fills with the
  // on-shift roster name (or PIN staff if set) but staff can change before saving.
  const quickComplete = async (task) => {
    const onShiftName = data?.on_shift_now?.staff_name || '';
    const pinName = activeStaff?.name || activeStaff?.first_name || '';
    const defaultName = pinName || onShiftName || user?.username || 'Staff';
    const who = window.prompt(
      `Who's marking "${task.title}" as done?\n\n` +
      (onShiftName ? `Roster says ${onShiftName} is on shift right now.\n\n` : '') +
      'Type your name:',
      defaultName
    );
    if (!who || !who.trim()) return;
    setBusy(task.id);
    try {
      await axios.post(`${API_URL}/staff/tasks/${task.id}/complete`, {
        completed_by_name: who.trim(),
      }, { headers: getAuthHeader() });
      toast.success(`✓ ${task.title} — by ${who.trim()}`);
      fetchTasks();
    } catch {
      toast.error('Could not mark done');
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  const allTasks = [...(data.overdue || []), ...(data.tasks || [])];
  const open = allTasks.filter(t => !t.completed);
  const completed = allTasks.filter(t => t.completed).length;
  const total = allTasks.length;
  // Sort: overdue → call_customer → priority high→low
  const priorityRank = { high: 0, medium: 1, low: 2 };
  open.sort((a, b) => {
    if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
    if ((a.category === 'call_customer') !== (b.category === 'call_customer')) return a.category === 'call_customer' ? -1 : 1;
    return (priorityRank[a.priority] ?? 1) - (priorityRank[b.priority] ?? 1);
  });
  const top = open.slice(0, 5);
  const remaining = open.length - top.length;

  if (total === 0) return null;

  return (
    <Card className="border-2 border-amber-200 bg-amber-50/40" data-testid="dashboard-tasks-widget">
      <CardHeader className="py-3 px-4 border-b border-amber-200 bg-amber-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-900 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Today's Tasks
            <Badge variant="outline" className="bg-white text-amber-800 border-amber-300 ml-1">
              {completed}/{total} done
            </Badge>
            {data.on_shift_now && (
              <span className="text-xs font-normal text-amber-700 normal-case ml-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {data.on_shift_now.staff_name}'s shift ({data.on_shift_now.start}–{data.on_shift_now.end})
              </span>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate('/staff-tasks')}
            className="text-amber-800 hover:text-amber-900 hover:bg-amber-100 h-7"
            data-testid="dashboard-view-all-tasks"
          >
            View all <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-2">
        {top.length === 0 ? (
          <p className="text-center text-sm text-emerald-700 py-3 font-medium">🎉 All done for today — nice work!</p>
        ) : (
          <div className="space-y-1">
            {top.map(task => (
              <div
                key={task.id}
                className={`flex items-center gap-2 p-2 rounded ${task.is_overdue ? 'bg-red-50' : task.category === 'call_customer' ? 'bg-emerald-50' : 'hover:bg-amber-100/50'}`}
                data-testid={`dashboard-task-${task.id}`}
              >
                <button
                  type="button"
                  onClick={() => quickComplete(task)}
                  disabled={busy === task.id}
                  className="shrink-0"
                  title="Mark as done"
                  data-testid={`dashboard-task-check-${task.id}`}
                >
                  <Circle className="w-5 h-5 text-slate-400 hover:text-emerald-600 transition-colors" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                    {task.is_overdue && <Badge className="bg-red-600 text-white text-[9px] font-bold">OVERDUE</Badge>}
                    {task.priority === 'high' && !task.is_overdue && <Badge className="bg-red-500 text-white text-[9px] font-bold">HIGH</Badge>}
                  </div>
                  {task.customer_phone && (
                    <a
                      href={`tel:${task.customer_phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-0.5"
                    >
                      <Phone className="w-3 h-3" /> {task.customer_phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => navigate('/staff-tasks')}
                className="w-full text-center text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100 py-1.5 rounded font-medium"
              >
                + {remaining} more {remaining === 1 ? 'task' : 'tasks'} — open full list
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { getAuthHeader, activeStaff, hasPermission } = useAuth();
  const { selectedStore, getCurrentStore, storeName: storeNameOf, isMultiStore } = useStore();
  const currentStore = getCurrentStore();
  const storeName = currentStore?.name || storeNameOf(selectedStore);
  const isOwner = (activeStaff?.name || '').trim().toLowerCase() === 'ritu' || activeStaff?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [monthlySales, setMonthlySales] = useState(null);
  const [conditionSales, setConditionSales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, salesRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/stats`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/dashboard/monthly-sales`, { headers: getAuthHeader() })
        ]);
        setStats(statsRes.data);
        setMonthlySales(salesRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    axios.get(`${API_URL}/reports/condition-sales?days=30`, { headers: getAuthHeader() })
      .then(res => setConditionSales(res.data))
      .catch(() => {});
  }, [getAuthHeader]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  const currentStoreSalesKey = selectedStore === 'eastridge' ? 'today_sales_eastridge' : 'today_sales_orewa';
  const otherStoreCode = selectedStore === 'eastridge' ? 'orewa' : 'eastridge';
  const otherStoreName = storeNameOf(otherStoreCode);
  const otherStoreSalesKey = otherStoreCode === 'eastridge' ? 'today_sales_eastridge' : 'today_sales_orewa';

  // Main top-row KPIs: focused on sales activity for the day
  const salesCards = [
    {
      title: `Today's Sales — ${storeName}`,
      value: `$${(stats?.[currentStoreSalesKey] || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      highlight: true
    },
    ...(isMultiStore ? [{
      title: `Today's Sales — ${otherStoreName}`,
      value: `$${(stats?.[otherStoreSalesKey] || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-slate-500',
      bgColor: 'bg-slate-100'
    }] : []),
    {
      title: 'Total Sales Today',
      value: `$${(stats?.today_sales || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50'
    },
    {
      title: 'Units Sold',
      value: stats?.total_units_sold || 0,
      icon: ShoppingCart,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  // Sidebar inventory KPIs: stacked on the right
  const inventoryCards = [
    {
      title: 'Total Products',
      value: (stats?.total_products || 0).toLocaleString(),
      icon: Package,
      color: 'text-slate-700',
      bgColor: 'bg-slate-100'
    },
    {
      title: 'Units in Stock',
      value: (stats?.total_units || 0).toLocaleString(),
      icon: BarChart3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Inventory Value',
      value: `$${(stats?.total_inventory_value || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50'
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_count || 0,
      subtitle: 'in stock, under 10 units',
      icon: AlertTriangle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-sm p-3 shadow-lg">
          <p className="font-heading font-semibold text-slate-900 uppercase text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${entry.value.toLocaleString()} ({entry.payload[`${entry.dataKey.replace('Year', 'YearUnits')}`]} units)
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header - Store-aware */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B5E20]" style={{fontFamily: 'Work Sans, sans-serif'}} data-testid="dashboard-title">
            {storeName} Dashboard
          </h1>
          <p className="text-[#558B2F] mt-1 text-sm">Live overview of {storeName} store</p>
        </div>
        <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-[#E8F5E9] text-[#1B5E20] border border-[#C5E1A5]" data-testid="dashboard-store-badge">
          <span className="w-2 h-2 rounded-full bg-[#1B5E20]"></span>
          Viewing: {storeName}
        </span>
      </div>

      {/* Upcoming Time Off banner — visible to everyone (owner + staff). Dismissible per day, reappears tomorrow until the leave date passes or alert is resolved. */}
      <UpcomingLeaveBanner />

      {/* Never-out watch — top sellers out of stock / running low */}
      <NeverOutAlert />

      {/* Today's Tasks widget — for staff. Hidden when Ritu (owner) is signed in. */}
      {!isOwner && <TodaysTasksWidget store={selectedStore} />}

      {/* Two-column layout: main content + inventory sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6 items-start">
        {/* MAIN COLUMN */}
        <div className="space-y-6 min-w-0">
          {/* Sales KPI Row */}
          <div className={`grid grid-cols-2 sm:grid-cols-2 ${salesCards.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4`} data-testid="sales-kpi-cards">
            {salesCards.map((card, index) => (
              <Card
                key={index}
                className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-all ${card.highlight ? 'border-[#1B5E20] ring-1 ring-[#1B5E20]/20' : 'border-[#E8F5E9] hover:border-[#C5E1A5]'}`}
                data-testid={`sales-kpi-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bgColor}`}>
                      <card.icon className={`w-5 h-5 ${card.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1B5E20]">{card.value}</p>
                  <p className="text-xs text-[#558B2F] mt-1 font-medium">{card.title}</p>
                  {card.subtitle && <p className="text-[10px] text-slate-400">{card.subtitle}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Monthly Sales Chart */}
          <Card className="bg-white border border-[#E8F5E9] rounded-xl shadow-sm" data-testid="monthly-sales-chart">
            <CardHeader className="pb-2 border-b border-[#E8F5E9]">
              <CardTitle className="text-lg font-semibold text-[#1B5E20]" style={{fontFamily: 'Work Sans, sans-serif'}}>
                24 Month Sales Comparison
              </CardTitle>
              <p className="text-sm text-[#558B2F]">
                {monthlySales?.lastYear} vs {monthlySales?.currentYear} - Monthly Revenue
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="h-[350px] mt-4" style={{ minWidth: 300, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={monthlySales?.data || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8F5E9" vertical={false} />
                    <XAxis 
                      dataKey="month" 
                      tickLine={false} 
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                    />
                    <YAxis 
                      tickLine={false} 
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend 
                      wrapperStyle={{ paddingTop: 20 }}
                      formatter={(value) => (
                        <span className="text-sm text-slate-600">
                          {value === 'lastYear' ? `${monthlySales?.lastYear}` : `${monthlySales?.currentYear}`}
                        </span>
                      )}
                    />
                    <Bar 
                      dataKey="lastYear" 
                      name="lastYear"
                      fill="#CBD5E1" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Bar 
                      dataKey="currentYear" 
                      name="currentYear"
                      fill="#0F172A" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Units Sold Chart */}
          <Card className="chart-container" data-testid="units-sold-chart">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
                Units Sold Per Month
              </CardTitle>
              <p className="text-sm text-slate-500">
                Comparing units sold {monthlySales?.lastYear} vs {monthlySales?.currentYear}
              </p>
            </CardHeader>
            <CardContent>
              <div className="h-[350px] mt-4" style={{ minWidth: 300, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={monthlySales?.data || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis 
                      dataKey="month" 
                      tickLine={false} 
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                    />
                    <YAxis 
                      tickLine={false} 
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                    />
                    <Tooltip cursor={{ fill: 'transparent' }} />
                    <Legend 
                      wrapperStyle={{ paddingTop: 20 }}
                      formatter={(value) => (
                        <span className="text-sm text-slate-600">
                          {value === 'lastYearUnits' ? `${monthlySales?.lastYear} Units` : `${monthlySales?.currentYear} Units`}
                        </span>
                      )}
                    />
                    <Bar 
                      dataKey="lastYearUnits" 
                      name="lastYearUnits"
                      fill="#F97316" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                      opacity={0.5}
                    />
                    <Bar 
                      dataKey="currentYearUnits" 
                      name="currentYearUnits"
                      fill="#F97316" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SIDEBAR - Inventory snapshot */}
        <aside className="space-y-3 lg:sticky lg:top-4" data-testid="inventory-sidebar">
          {hasPermission('admin') && <TillTodayCard />}
          <p className="text-xs font-semibold uppercase tracking-wider text-[#558B2F] px-1">Inventory snapshot</p>
          {inventoryCards.map((card, index) => (
            <Card
              key={index}
              className="bg-white border border-[#E8F5E9] rounded-xl shadow-sm hover:shadow-md hover:border-[#C5E1A5] transition-all"
              data-testid={`inventory-sidebar-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${card.bgColor}`}>
                    <card.icon className={`w-5 h-5 ${card.color}`} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xl font-bold text-[#1B5E20] leading-tight truncate">{card.value}</p>
                    <p className="text-xs text-[#558B2F] mt-0.5 font-medium">{card.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {conditionSales && conditionSales.conditions?.length > 0 && (
            <Card className="bg-white border border-[#E8F5E9] rounded-xl shadow-sm" data-testid="condition-sales-card">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-2">
                  Condition Picks Revenue (30d) — ${conditionSales.total_revenue.toFixed(2)}
                </p>
                <div className="space-y-2">
                  {conditionSales.conditions.slice(0, 8).map(c => (
                    <div key={c.condition} className="text-sm">
                      <div className="flex justify-between font-medium text-[#1B5E20]">
                        <span>{c.condition}</span>
                        <span>${c.revenue.toFixed(2)} · {c.units}u</span>
                      </div>
                      {c.top_products.slice(0, 2).map(p => (
                        <p key={p.name} className="text-xs text-slate-500 truncate pl-2">— {p.name} ({p.units})</p>
                      ))}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;
